import upgradeUiCatalog from "@/constants/upgradeUiCatalog";
import { computed, ref } from "vue";

// Module-scoped reactive state
const plan = ref("pro");
const context = ref({
    requiredPlan: "pro",
    contextKey: null,
    featureName: "",
    source: "",
    pattern: "",
});
let modalRef = null;

const normalizePlan = (value) => {
    const v = (value || "pro").toString().toLowerCase();
    return v === "premium" ? "premium" : "pro";
};

const titleCasePlan = (value) => (normalizePlan(value) === "premium" ? "Premium" : "Pro");

const interpolateTemplate = (template, values = {}) =>
    String(template || "").replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) =>
        values[key] == null ? "" : String(values[key])
    );

const fallbackContext = (requiredPlan, request = {}) => {
    const planLabel = titleCasePlan(requiredPlan);
    const featureName = request.featureName || "Feature";
    const pattern = request.pattern || "";
    const patternValueText = {
        "content-overlay": "Get access to advanced insights and controls.",
        "locked-action":
            "Upgrade to use this action and continue building your page.",
        "locked-setting":
            "Upgrade to enable this setting and customize your page.",
        "limit-notice": "Upgrade to raise your current plan limit.",
    };
    return {
        requiredPlan,
        contextKey: request.contextKey || null,
        featureName,
        title: `Unlock ${featureName}`,
        description: `${featureName} is available on ${planLabel}. ${
            patternValueText[pattern] || "Upgrade to continue."
        }`,
        primaryCtaLabel: `Upgrade to ${planLabel}`,
        pattern,
        source: request.source || "",
    };
};

const resolveCatalogContext = (request = {}) => {
    const key = request.contextKey || null;
    const requestedPlan = normalizePlan(request.requiredPlan || request.plan || "pro");
    const catalogEntry = key ? upgradeUiCatalog[key] : null;
    const requiredPlan = normalizePlan(catalogEntry?.requiredPlan || requestedPlan);
    const planLabel = titleCasePlan(requiredPlan);
    if (!catalogEntry) {
        return fallbackContext(requiredPlan, request);
    }

    const featureName = request.featureName || catalogEntry.featureName || "Feature";
    const title =
        request.featureName && catalogEntry.title?.startsWith("Unlock ")
            ? `Unlock ${request.featureName}`
            : catalogEntry.title || `Unlock ${featureName}`;
    let description =
        request.featureName && typeof catalogEntry.descriptionTemplate === "string"
            ? interpolateTemplate(catalogEntry.descriptionTemplate, {
                  featureName,
                  planLabel,
              })
            : catalogEntry.description;
    if (
        request.featureName &&
        !catalogEntry.descriptionTemplate &&
        typeof description === "string"
    ) {
        description = description.replace(
            /(Premium fonts|Premium themes|Themes|Products|More Fonts|Code Lock|Analytics|Digital Cards|App Integrations|Premium App Integrations)/,
            request.featureName
        );
    }

    return {
        requiredPlan,
        contextKey: key,
        featureName,
        title,
        description:
            description ||
            `${featureName} is available on ${planLabel}. Upgrade to continue.`,
        primaryCtaLabel:
            catalogEntry.primaryCtaLabel || `Upgrade to ${planLabel}`,
        pattern: request.pattern || catalogEntry.pattern || "",
        source: request.source || "",
    };
};

/**
 * Register the UpgradeModal component instance
 * Called once by the global UpgradeModal in Layout.vue
 * @param {Object} componentRef - The modal component ref with showModal/closeModal methods
 */
export const registerUpgradeModal = (componentRef) => {
    modalRef = componentRef;
};

/**
 * Unregister the modal (cleanup on unmount)
 */
export const unregisterUpgradeModal = () => {
    modalRef = null;
};

/**
 * Show the upgrade modal with optional plan override
 * @param {string} targetPlan - 'pro' or 'premium' (defaults to 'pro')
 */
export const showUpgradeModal = (request = "pro") => {
    if (typeof request === "string") {
        const requiredPlan = normalizePlan(request);
        plan.value = requiredPlan;
        context.value = {
            requiredPlan,
            contextKey: null,
            featureName: "",
            source: "",
            pattern: "",
        };
        modalRef?.showModal?.();
        return;
    }

    const resolved = resolveCatalogContext(request || {});
    plan.value = resolved.requiredPlan;
    context.value = {
        requiredPlan: resolved.requiredPlan,
        contextKey: resolved.contextKey,
        featureName: resolved.featureName,
        source: resolved.source,
        pattern: resolved.pattern,
    };
    modalRef?.showModal?.();
};

/**
 * Close the upgrade modal
 */
export const closeUpgradeModal = () => {
    modalRef?.closeModal?.();
};

/**
 * Get current plan value (reactive)
 * @returns {import('vue').Ref<string>}
 */
export const getUpgradePlan = () => plan;

/**
 * Get current contextual upgrade request details (reactive)
 */
export const getUpgradeContext = () => context;

/**
 * Resolve display copy for the current upgrade request (reactive)
 */
export const getResolvedUpgradeContent = () =>
    computed(() => {
        const current = context.value || {};
        if (!current.contextKey && !current.featureName) {
            return null;
        }
        return resolveCatalogContext({
            ...current,
            requiredPlan: current.requiredPlan || plan.value,
        });
    });
